import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from "@angular/core";
import { ActivatedRoute, MaybeAsync, ParamMap, Router } from "@angular/router";
import { Subject } from "rxjs";

import { Command } from "@tylertech/commands-module";
import { FormMetadataService, OmniBarService, SetupViewService } from "@tylertech/core-module";
import { getLoggerFor, LogInvocation, LogLevel } from "@tylertech/logging";
import { ODataFilterBuilder } from "@tylertech/odata-module";
import { ErrorType, Utilities } from "@tylertech/shared-module";
import { IFormMetadata } from "@tylertech/tyler-api-types";

import { FormTitleService, MultipleEntityEditService } from "../../services";
import { MetadataFormComponent } from "../metadataForm/metadata.form.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

const enum MetaFormQueryParam
{
    EditFormId = "editFormId",
    FormType = "formType",
    LazyLoadFormFilter = "lazyLoadFormFilter",
    LazyLoadFormType = "lazyLoadFormType",
    OdataFilter = "$filter",
    Passback = "passback"
}

@Component({
    selector: "meta-form",
    templateUrl: "./metaform.tmpl.html",
    standalone: false
})
export class MetaFormComponent implements OnChanges, OnInit
{
    /** To fetch FormMetadata */
    @Input() readonly path: string;

    /** To fetch FormMetadata */
    @Input() readonly formName: string;

    /** Passthrough */
    @Input() readonly initialView: string;
    @Input() readonly preload: string;
    /** Passthrough */
    @Input() readonly newForm: boolean;
    /** Passthrough */
    @Input() readonly navigateBackOnReturn: boolean;
    /** Passthrough */
    @Input() readonly closeOnFinish: boolean;
    /** Passthrough */
    @Input() readonly customViewCommands: ReadonlyArray<Command>;

    @ViewChild(MetadataFormComponent) private readonly metadataFormComponent: MetadataFormComponent;

    private readonly log = getLoggerFor("appMetaFormComponent");
    private isInitialized: boolean;
    private editFormId?: string;
    private queryParamFormType?: string;
    private queryParamLazyLoadFormType?: string;
    private queryParamLazyLoadFormFilter?: string;
    private $filter?: `$filter=${string}`;
    private loading?: Promise<void>;
    private passback: string;

    createNewModelOnLoad: boolean;
    error: boolean;
    errorType: ErrorType;
    form: IFormMetadata;

    constructor(
        private activatedRoute: ActivatedRoute,
        private readonly router: Router,
        $omnibarService: OmniBarService,
        private readonly $formMetadataService: FormMetadataService,
        private readonly $formTitleService: FormTitleService,
        private readonly $setupViewService: SetupViewService,
        private readonly $multipleEntityEditService: MultipleEntityEditService,
    )
    {
        activatedRoute.queryParamMap
            .pipe(takeUntilDestroyed())
            .subscribe((paramMap: ParamMap) =>
            {
                this.$filter = paramMap.has(MetaFormQueryParam.OdataFilter)
                    ? `$filter=${paramMap.get(MetaFormQueryParam.OdataFilter)}`
                    : undefined;

                const editFormId = paramMap.get(MetaFormQueryParam.EditFormId);
                this.editFormId = editFormId;

                const formType = paramMap.get(MetaFormQueryParam.FormType);
                const formTypeChange = this.queryParamFormType !== formType;
                this.queryParamFormType = formType;

                const lazyLoadFormFilter = paramMap.get(MetaFormQueryParam.LazyLoadFormFilter);
                const lazyLoadFormFilterChange = this.queryParamLazyLoadFormFilter !== lazyLoadFormFilter;
                this.queryParamLazyLoadFormFilter = lazyLoadFormFilter;

                const lazyLoadFormType = paramMap.get(MetaFormQueryParam.LazyLoadFormType);
                const lazyLoadFormTypeChange = this.queryParamLazyLoadFormType !== lazyLoadFormType;
                this.queryParamLazyLoadFormType = lazyLoadFormType;

                const passback = paramMap.get(MetaFormQueryParam.Passback);
                this.passback = passback;

                //TODO: we may not need this since this component cycles on a route change, which runs reloadForm already
                // if (this.isInitialized
                //     && (formTypeChange
                //         || lazyLoadFormFilterChange
                //         || lazyLoadFormTypeChange))
                // {
                //     this.reloadForm();
                // }
            });

        this.$multipleEntityEditService.onAddNewSeparateForm.pipe(takeUntilDestroyed())
            .subscribe(() => this.reloadAsNew());
    }

    reloadAsNew(): void
    {
        const current = { ...this.activatedRoute.snapshot.queryParams };
        delete current[MetaFormQueryParam.EditFormId];
        this.router.navigate([],
        {
            relativeTo: this.activatedRoute,
            queryParams: current,
            replaceUrl: true
        })
        .then(() => this.reloadForm(true));
    }

    get loaded(): boolean
    {
        return !this.loading;
    }

    ngOnInit(): void
    {
        //Direct Edit Form
        this.isInitialized = true;
        this.reloadForm();
    }

    ngOnChanges(changes: SimpleChanges): void
    {
        if (!this.isInitialized
            || (!changes.formName
                && !changes.path))
        {
            return;
        }
        const pathChanged = changes.path?.previousValue !== changes.path?.currentValue;
        const formNameChanged = changes.formName?.previousValue !== changes.formName?.currentValue;
        if (pathChanged || formNameChanged)
        {
            this.reloadForm();
        }
    }

    canDeactivateRoute(): MaybeAsync<boolean>
    {
        if (!this.metadataFormComponent)
        {
            return true;
        }

        return this.metadataFormComponent.canDeactivateRoute();
    }

    @LogInvocation(LogLevel.DEBUG)
    private reloadForm(clearFilter: boolean = false): Promise<void>
    {
        if (!this.loading)
        {
            this.loading = this.loadForm(clearFilter)
                .then((formMetadata: IFormMetadata | undefined) =>
                {
                    this.$formTitleService.setTitle(formMetadata?.Name || "Form");

                    this.form = formMetadata;
                })
                .finally(() =>
                {
                    this.loading = undefined;
                });
        }

        return this.loading;
    }

    @LogInvocation(LogLevel.DEBUG, true)
    private loadForm(clearFilter: boolean): Promise<IFormMetadata | void>
    {
        if (this.queryParamFormType)
        {
            return this.loadFromFormType();
        }
        else if (this.queryParamLazyLoadFormType)
        {
            return this.loadFromLazyLoadFormType();
        }
        else if (this.formName)
        {
            return this.loadFromPathAndFormName();
        }
        else if (this.path)
        {
            return this.loadFromPath(clearFilter);
        }

        return Promise.resolve();
    }

    private loadFromFormType(): Promise<IFormMetadata>
    {
        let path = this.path;
        if (this.formName)
        {
            path += `/${this.formName}`;
        }
        return this.$formMetadataService.GetEditForm(
            {
                Item1: path,
                Item2: this.queryParamFormType,
            })
            .then(r =>
            {
                if (!r)
                {
                    this.error = true;
                    this.errorType = ErrorType.NoPermission;
                }

                if (!this.editFormId
                    || !this.$filter
                    || !this.preload)
                {
                    this.createNewModelOnLoad = true;
                }

                return r;
            });
    }

    private loadFromLazyLoadFormType(): Promise<IFormMetadata>
    {
        return this.$formMetadataService.GetByType(this.queryParamLazyLoadFormType, this.queryParamLazyLoadFormFilter)
            .then(r =>
            {
                if (!r)
                {
                    this.error = true;
                    this.errorType = ErrorType.NoPermission;
                }

                if (!this.editFormId
                    || !this.preload)
                {
                    this.createNewModelOnLoad = true;
                }

                return r;
            });
    }

    private loadFromPathAndFormName(): Promise<IFormMetadata>
    {
        const odataFilter = new ODataFilterBuilder<IFormMetadata>()
            // TODO - why are we using encodeUriComponent?
            .filterEQ("Path", this.path.replace(/&/g, "%26"))
            .filterEQ("Name", this.formName.replace(/&/g, "%26"))
            .build();

        return this.$setupViewService.Get(odataFilter)
            .then(r =>
            {
                if (r.length === 0)
                {
                    this.error = true;
                    this.errorType = ErrorType.NotFound;
                    return undefined;
                }

                return r[0];
            });
    }

    private loadFromPath(clearFilter: boolean): Promise<IFormMetadata>
    {
        if (clearFilter)
        {
            const params = { ...this.activatedRoute.snapshot.queryParams };
            delete params[MetaFormQueryParam.OdataFilter];
            this.router.navigate([], { queryParams: params });
        }
        const filter = this.$filter && !clearFilter
            ? this.$filter.indexOf("$filter") > -1 ? this.$filter : `$filter=${this.$filter}`
            : undefined;
        return this.$formMetadataService.GetForm(this.path, filter)
            .then(r =>
            {
                if (!r)
                {
                    this.error = true;
                    this.errorType = ErrorType.NoPermission;
                }

                if (!filter || filter?.length === 0 || (!Utilities.IsNullOrUndefined(this.preload) && this.preload !== "false"))
                {
                    this.createNewModelOnLoad = true;
                }

                return r;
            });
    }
}
